<template>
  <div>
    <!-- Toolbar -->
    <v-row align="center" class="mb-4" dense>
      <!-- Vista appezzamento: selettore -->
      <v-col v-if="vista === 'appezzamento'">
        <v-select
          v-model="appezzamento_selezionato"
          :items="appezzamenti"
          item-text="nome"
          return-object
          label="Seleziona appezzamento"
          outlined
          dense
          hide-details
          clearable
          :disabled="!appezzamenti.length"
        >
          <template #item="{ item }">
            <div class="py-1">
              <div class="text-body-2 font-weight-medium">{{ item.nome }}</div>
              <div class="caption grey--text">{{ item.larghezza || '?' }}×{{ item.lunghezza || '?' }} m</div>
            </div>
          </template>
        </v-select>
      </v-col>

      <!-- Vista serra: breadcrumb -->
      <v-col v-else class="d-flex align-center" style="gap:4px">
        <v-btn text small @click="vista = 'appezzamento'">
          <v-icon small class="mr-1">mdi-arrow-left</v-icon>
          {{ appezzamento_selezionato ? appezzamento_selezionato.nome : 'Appezzamento' }}
        </v-btn>
        <span class="grey--text text--lighten-1 mx-1">/</span>
        <span class="text-body-2 font-weight-medium">{{ serra_selezionata ? serra_selezionata.nome : '' }}</span>
      </v-col>

      <!-- Badge layout (solo vista appezzamento) -->
      <v-col v-if="vista === 'appezzamento' && badge_label" cols="auto" class="d-flex align-center">
        <v-chip x-small :color="badge_color" dark label class="font-weight-medium">
          <v-icon x-small class="mr-1">{{ badge_icon }}</v-icon>
          {{ badge_label }}
        </v-chip>
      </v-col>

      <!-- Pulsanti CRUD -->
      <v-col cols="auto" class="d-flex" style="gap:8px">
<v-btn outlined small color="teal darken-1" @click="$refs.confronto_dialog.open()" :disabled="!stagione_attiva">
          <v-icon small class="mr-1">mdi-clipboard-check-outline</v-icon>
          Confronto
        </v-btn>
        <v-btn outlined small color="green darken-1" @click="vista === 'serra' && serra_selezionata ? onModificaSerra(serra_selezionata.id) : $refs.gestione_serre.openDialog()">
          <v-icon small class="mr-1">mdi-greenhouse</v-icon>
          Serre
        </v-btn>
      </v-col>
    </v-row>

    <!-- ========== VISTA APPEZZAMENTO ========== -->
    <template v-if="vista === 'appezzamento'">
      <!-- Nessun appezzamento -->
      <v-alert
        v-if="!appezzamenti.length"
        type="info"
        outlined
      >
        Nessun appezzamento configurato. Clicca su "Appezzamenti" per crearne uno.
      </v-alert>

      <!-- Appezzamento selezionato -->
      <template v-else-if="appezzamento_selezionato">
        <v-progress-linear v-if="loading_posizioni" indeterminate class="mb-2" />

        <!-- Layout non caricabile: non ricadere sulla mappa aziendale -->
        <v-alert v-else-if="stagione_attiva && layout_errore && !layout_corrente" type="error" outlined class="mb-2">
          <div class="font-weight-medium mb-1">Mappa stagionale non caricata.</div>
          <div class="text-body-2 mb-3">
            Non è stato possibile leggere il layout di questa stagione. La mappa aziendale non
            viene mostrata al suo posto per evitare modifiche accidentali.
          </div>
          <v-btn small color="error" outlined @click="riprovaCaricamentoLayout">
            <v-icon small class="mr-1">mdi-refresh</v-icon>
            Riprova
          </v-btn>
        </v-alert>

        <!-- Nessun fork: banner per importare dalla mappa aziendale -->
        <v-alert v-else-if="stagione_attiva && !layout_corrente" type="info" outlined class="mb-2">
          <div class="font-weight-medium mb-1">Mappa non ancora configurata per questa stagione.</div>
          <div class="text-body-2 mb-3">
            Importa la configurazione dalla mappa aziendale per iniziare. Da quel momento le due mappe saranno indipendenti.
          </div>
          <v-btn small color="primary" :loading="loading_fork" @click="onForkMappa">
            <v-icon small class="mr-1">mdi-source-fork</v-icon>
            Importa mappa aziendale
          </v-btn>
        </v-alert>

        <template v-else>
          <v-alert
            v-if="layout_corrente && layout_corrente.migrato"
            type="warning" dense outlined class="mb-2"
          >
            Layout storico approssimativo: la disposizione potrebbe non riflettere la situazione reale di questa stagione.
          </v-alert>
          <AppezzamentoCanvas
            :appezzamento="appezzamento_selezionato"
            :posizioni="posizioni_canvas"
            :serre="serre_canvas"
            :blocchi="blocchi_canvas"
            :readonly="!layout_corrente"
            :fork_mode="!!layout_corrente"
            :can_edit_blocchi="!layout_corrente"
            @serra-selezionata="onSerraSelezionataAppezzamento"
            @posizione-aggiunta="onPosizioneAppezzamentoAggiunta"
            @posizione-aggiornata="onPosizioneAppezzamentoAggiornata"
            @posizione-rimossa="onPosizioneAppezzamentoRimossa"
            @modifica-serra="onModificaSerra"
            :completamento_serre="completamento_serre"
            :recap_per_serra="recap_per_serra"
            :file_per_serra="file_per_serra"
            :colori_per_fila="colori_per_fila"
            @blocco-aggiunto="onBloccoAggiunto"
            @blocco-aggiornato="onBloccoAggiornato"
            @blocco-rimosso="onBloccoRimosso"
          />
        </template>
      </template>

      <!-- Nessun appezzamento selezionato -->
      <v-alert v-else outlined>
        Seleziona un appezzamento dal menu sopra per visualizzare la mappa.
      </v-alert>
    </template>

    <!-- ========== VISTA DETTAGLIO SERRA ========== -->
    <template v-else>
      <!-- Caricamento snapshot -->
      <v-progress-linear v-if="loading_snapshot" indeterminate class="mb-2" />

      <template v-else-if="serra_selezionata">
        <SerraMappaView
          :serra="serra_selezionata"
          :tipo="serra_selezionata ? serra_selezionata.tipo : null"
          :file_stagione="file_stagione_corrente"
          :specie="specie_disponibili"
          :snapshot="snapshot"
          :prodotti_pianificazione="prodotti_pianificazione"
          :prodotto_labels="prodotto_labels"
          :contenitori="contenitori_coltivazione"
          @fila-selezionata="onFilaSelezionata"
          @modifica-serra="onModificaSerra(serra_selezionata.id)"
        />

        <!-- Recap prodotti -->
        <v-card v-if="recap_prodotti.righe.length" outlined class="mt-4 mx-auto" style="max-width:800px">
          <v-simple-table dense>
            <thead>
              <tr>
                <th class="caption grey--text" colspan="2">Prodotto</th>
                <th class="caption grey--text text-right">Ø</th>
                <th class="caption grey--text text-right">Vasi</th>
                <th class="caption grey--text text-right">%</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in recap_prodotti.righe" :key="item.id">
                <td style="width:36px; padding-right:0">
                  <template v-if="!item.component_infos || item.component_infos.length <= 1">
                    <img v-if="item.img_url" :src="item.img_url" class="legenda-swatch" />
                    <div v-else class="legenda-swatch" :style="{ backgroundColor: item.color }" />
                  </template>
                  <svg v-else class="legenda-swatch" viewBox="0 0 28 28" width="28" height="28">
                    <defs>
                      <clipPath
                        v-for="(inf, idx) in item.component_infos"
                        :key="`cprp-${item.id}-${idx}`"
                        :id="`prp-${item.id}-${idx}`"
                      >
                        <path :d="_sectorPath(idx, item.component_infos.length, 14, 14, 14)"/>
                      </clipPath>
                    </defs>
                    <g v-for="(inf, idx) in item.component_infos" :key="`secrp-${item.id}-${idx}`">
                      <image v-if="inf.img_url" :href="inf.img_url" x="0" y="0" width="28" height="28"
                             :clip-path="`url(#prp-${item.id}-${idx})`" preserveAspectRatio="xMidYMid slice"/>
                      <path v-else :d="_sectorPath(idx, item.component_infos.length, 14, 14, 14)"
                            :fill="inf.fallback"/>
                    </g>
                    <circle cx="14" cy="14" r="13.5" fill="none" stroke="rgba(0,0,0,0.15)" stroke-width="1"/>
                  </svg>
                </td>
                <td style="line-height:1.3; padding-top:6px; padding-bottom:6px">
                  <div v-if="item.tipo_contenitore" class="text-caption grey--text text--darken-1" style="line-height:1;margin-bottom:1px;">{{ item.tipo_contenitore }}</div>
                  <div v-if="item.specie_nomi" class="text-caption grey--text font-italic" style="line-height:1; margin-bottom: 5px;">{{ item.specie_nomi }}</div>
                  <div v-for="(riga, ri) in item.righe_dettaglio" :key="ri" class="text-body-2 d-flex align-center" style="gap:5px">
                    <span
                      :title="riga.hex || 'Nessun HEX'"
                      :style="{
                        display: 'inline-block',
                        width: '12px', height: '12px',
                        borderRadius: '3px',
                        border: '1px solid rgba(0,0,0,0.18)',
                        flexShrink: 0,
                        background: riga.hex
                          ? riga.hex
                          : 'linear-gradient(to bottom right, #fff 0%, #fff calc(50% - 0.8px), #e53935 calc(50% - 0.8px), #e53935 calc(50% + 0.8px), #fff calc(50% + 0.8px), #fff 100%)',
                      }"
                    />
                    {{ riga.testo }}
                  </div>
                  <div v-if="!item.righe_dettaglio || !item.righe_dettaglio.length" class="text-body-2">{{ item.label }}</div>
                </td>
                <td class="text-body-2 text-right grey--text" style="white-space:nowrap">{{ item.diametro ? `Ø${item.diametro}` : '—' }}</td>
                <td class="text-body-2 text-right">{{ item.vasi }}</td>
                <td class="text-body-2 text-right">{{ item.pct }}%</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="grey lighten-4">
                <td colspan="3" class="caption font-weight-medium">
                  Liberi: {{ recap_prodotti.liberi }} · Totale: {{ recap_prodotti.totale_vasi }}
                </td>
                <td class="caption font-weight-medium text-right">{{ recap_prodotti.occupati }}</td>
                <td class="caption font-weight-medium text-right">
                  {{ recap_prodotti.totale_vasi > 0 ? Math.round(recap_prodotti.occupati / recap_prodotti.totale_vasi * 100) : 0 }}%
                </td>
              </tr>
            </tfoot>
          </v-simple-table>
        </v-card>

        <!-- Hint interazione -->
        <div class="mt-3 caption grey--text text--lighten-1 text-center">
          <v-icon x-small class="mr-1">mdi-cursor-pointer</v-icon>
          Clicca su una fila per assegnare i prodotti
        </div>
      </template>
    </template>

    <!-- Snackbar feedback -->
    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" top timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>

    <!-- Dialog picker prodotto (solo in vista serra) -->
    <ProdottoPicker
      v-model="dialog_picker"
      :fila="fila_selezionata"
      :file="file_serra_ordinate"
      :snapshot_fila="snapshot_fila"
      :snapshot_fila_precedente="snapshot_fila_precedente"
      :prodotti_pianificazione="prodotti_pianificazione"
      :prodotto_labels="prodotto_labels"
      :specie="specie_disponibili"
      :contenitori="contenitori_coltivazione"
      :loading="loading_posizione"
      :save_error="errore_posizione"
      :save_success="successo_posizione"
      @naviga-fila="onFilaSelezionata"
      @posizione-salvata="onPosizioneSalvata"
      @posizione-eliminata="onPosizioneEliminata"
    />

    <!-- Dialog gestione serre -->
    <GestioneSerreDialog
      ref="gestione_serre"
      :stagione_attiva="stagione_attiva"
      :fork_context="fork_context_dialog"
      @serre-aggiornate="onSerreAggiornate"
    />

    <!-- Dialog confronto piano vs posizionamento -->
    <ConfruntoPianoPosizionamentoDialog
      ref="confronto_dialog"
      :righe="confronto_righe"
      :stagione="stagione_attiva"
    />
  </div>
</template>

<script>
import colorUtility from "../Mixin/colorUtility.js";
import specieLookup from "../Mixin/specieLookup.js";
import SerraMappaView from "./SerraMappaView.vue";
import ProdottoPicker from "./ProdottoPicker.vue";
import GestioneSerreDialog from "./GestioneSerreDialog.vue";
import AppezzamentoCanvas from "./AppezzamentoCanvas.vue";
import ConfruntoPianoPosizionamentoDialog from "./ConfruntoPianoPosizionamentoDialog.vue";

export default {
  name: "MappaSerreTab",
  components: {
    SerraMappaView,
    ProdottoPicker,
    GestioneSerreDialog,
    AppezzamentoCanvas,
    ConfruntoPianoPosizionamentoDialog,
  },
  mixins: [colorUtility, specieLookup],
  props: {
    stagione_attiva: { type: Object, default: null },
  },
  data() {
    return {
      /* navigazione */
      vista: "appezzamento",          // 'appezzamento' | 'serra'
      appezzamento_selezionato: null,
      serra_selezionata: null,
      fila_selezionata: null,

      /* dialog */
      dialog_picker: false,

      /* loading */
      loading_posizioni: false,
      loading_snapshot: false,
      loading_posizione:  false,
      successo_posizione: false,
      loading_fork: false,
      // true quando GET del layout stagionale fallisce: senza questo flag
      // layout_corrente resterebbe null e la vista ricadrebbe sulla mappa LIVE
      layout_errore: false,

      errore_posizione: "",
      snackbar: { visible: false, color: "success", message: "" },
    };
  },
  mounted() {
    if (this.stagione_attiva) {
      this.$store.dispatch('fetchRiepilogoPosizionamento', this.stagione_attiva.id);
    }
    // serre e appezzamenti già caricati da fetch_2 al login
    if (!this.appezzamento_selezionato && this.appezzamenti.length) {
      this.appezzamento_selezionato = this.appezzamenti[0];
    }
  },
  computed: {
    appezzamenti() {
      return this.$store.getters.getAppezzamenti || [];
    },
    serre() {
      return (this.$store.getters.getSerre || []).filter(s => !s.dismessa);
    },
    posizioni_appezzamento() {
      if (!this.appezzamento_selezionato) return [];
      return this.$store.getters.getPosizioniAppezzamento(this.appezzamento_selezionato.id);
    },
    blocchi_appezzamento() {
      if (!this.appezzamento_selezionato) return [];
      return this.$store.getters.getBlocchiAppezzamento(this.appezzamento_selezionato.id);
    },
    specie_disponibili() {
      return this.$store.getters.get_specie || [];
    },
    contenitori_coltivazione() {
      return this.$store.getters.getContenitoriColtivazione || [];
    },
    prodotti_pianificazione() {
      if (!this.stagione_attiva) return [];
      return this.$store.getters.getProdottiPianificazione(this.stagione_attiva.id);
    },
    snapshot() {
      if (!this.serra_selezionata || !this.stagione_attiva) return [];
      return this.$store.getters.getSerraSnapshot(
        this.serra_selezionata.id,
        this.stagione_attiva.id
      );
    },
    file_stagione_corrente() {
      if (!this.serra_selezionata || !this.stagione_attiva) return [];
      return this.$store.getters.getFileStagione(
        this.serra_selezionata.id,
        this.stagione_attiva.id
      );
    },
    snapshot_fila() {
      if (!this.fila_selezionata) return [];
      return this.snapshot.filter((p) => p.id_fila_stagione === this.fila_selezionata.id);
    },
    snapshot_fila_precedente() {
      if (!this.fila_selezionata) return [];
      const prev = this.file_stagione_corrente.find(
        (f) => f.numero === this.fila_selezionata.numero - 1
      );
      if (!prev) return [];
      return this.snapshot.filter((p) => p.id_fila_stagione === prev.id);
    },
    prodotto_map() {
      const map = {};
      for (const p of this.prodotti_pianificazione) map[p.id] = p;
      return map;
    },
    prodotto_labels() {
      const map = {};
      for (const p of this.prodotti_pianificazione) {
        const dettaglio = this.getComponentiDettaglio(p);
        const nomi = dettaglio.map((d) => d.label).join(" / ");
        const contenitore = this.contenitori_coltivazione.find(
          (c) => c.id === p.id_contenitore
        );
        const diam = contenitore ? ` Ø${contenitore.diametro}` : "";
        map[p.id] = (nomi || `P${p.id}`) + diam;
      }
      return map;
    },
    serre_ids_nel_layout() {
      if (this.layout_corrente) return this.layout_corrente.serre.map((s) => s.id_serra_ref);
      return this.posizioni_appezzamento.map((p) => p.id_serra);
    },
    completamento_serre() {
      if (!this.stagione_attiva) return {};
      const map = {};
      for (const id of this.serre_ids_nel_layout) {
        if (!this.$store.getters.isSnapshotCached(id, this.stagione_attiva.id)) continue;
        const file     = this.$store.getters.getFileStagione(id, this.stagione_attiva.id);
        const num_vasi = file.reduce((acc, f) => acc + (f.num_vasi || 0), 0);
        const filled   = this.$store.getters.getSerraSnapshot(id, this.stagione_attiva.id)
          .reduce((acc, p) => acc + (p.vaso_fine - p.vaso_inizio + 1), 0);
        map[id] = num_vasi > 0 ? Math.round((filled / num_vasi) * 100) : 0;
      }
      return map;
    },
    recap_per_serra() {
      if (!this.stagione_attiva) return {};
      const result = {};
      for (const id_serra of this.serre_ids_nel_layout) {
        if (!this.$store.getters.isSnapshotCached(id_serra, this.stagione_attiva.id)) continue;
        const snapshot = this.$store.getters.getSerraSnapshot(id_serra, this.stagione_attiva.id);
        const file = this.$store.getters.getFileStagione(id_serra, this.stagione_attiva.id);
        const totale_vasi = file.reduce((acc, f) => acc + (f.num_vasi || 0), 0);
        const ids_order = [];
        const per_prodotto = {};
        for (const p of snapshot) {
          const id = p.id_prodotto_pianificazione;
          if (per_prodotto[id] === undefined) { per_prodotto[id] = 0; ids_order.push(id); }
          per_prodotto[id] += p.vaso_fine - p.vaso_inizio + 1;
        }
        const occupati = ids_order.reduce((a, id) => a + per_prodotto[id], 0);
        const righe = ids_order.map((id) => {
          const prodotto = this.prodotto_map[id] || null;
          const vasi = per_prodotto[id];
          const cont = prodotto
            ? this.contenitori_coltivazione.find((c) => c.id === prodotto.id_contenitore)
            : null;
          const riepilogo = this._componenteRiepilogo(prodotto, cont);
          return {
            id,
            label: (this.prodotto_labels[id] || `P${id}`).replace(/\s*Ø\d+.*$/, ''),
            img_url: this._varietaImgUrl(prodotto),
            color: this.productColor(prodotto),
            component_infos: this._componenteInfos(prodotto),
            tipo_contenitore: riepilogo.tipo_contenitore,
            specie_nomi: riepilogo.specie_nomi,
            righe_dettaglio: riepilogo.righe,
            diametro: cont ? cont.diametro : null,
            vasi,
            pct: totale_vasi > 0 ? Math.round((vasi / totale_vasi) * 100) : 0,
          };
        }).sort((a, b) => b.vasi - a.vasi);
        result[id_serra] = { righe, occupati, totale_vasi, liberi: totale_vasi - occupati };
      }
      return result;
    },
    colori_per_fila() {
      if (!this.stagione_attiva) return {};
      const result = {};
      for (const id_serra of this.serre_ids_nel_layout) {
        if (!this.$store.getters.isSnapshotCached(id_serra, this.stagione_attiva.id)) continue;
        const snapshot = this.$store.getters.getSerraSnapshot(id_serra, this.stagione_attiva.id);
        // Mappa sparsa per fila: vaso (1-based, come vaso_inizio/vaso_fine e come
        // il v-for del dettaglio serra) → colori del prodotto che occupa quel vaso.
        // Più colori su un vaso = più varietà nello stesso vaso → spicchi.
        // Prodotti diversi lungo la fila restano vasi distinti a tinta unita.
        const per_fila = {}; // id_fila → { vaso → string[] }
        for (const pos of snapshot) {
          const id_fila = String(pos.id_fila_stagione);
          if (!per_fila[id_fila]) per_fila[id_fila] = {};
          const prodotto = this.prodotto_map[pos.id_prodotto_pianificazione] || null;
          const colori = this._coloriProdotto(prodotto);
          for (let v = pos.vaso_inizio; v <= pos.vaso_fine; v++) {
            per_fila[id_fila][v] = colori;
          }
        }
        result[id_serra] = per_fila;
      }
      return result;
    },
    confronto_righe() {
      if (!this.stagione_attiva) return [];
      const riepilogo = this.$store.getters.getRiepilogoPosizionamento(this.stagione_attiva.id);
      const vasi_per_prodotto = {};
      for (const r of riepilogo) {
        vasi_per_prodotto[r.id_prodotto_pianificazione] = r.vasi_posizionati;
      }
      return this.prodotti_pianificazione.map((prodotto) => {
        const posizionato = vasi_per_prodotto[prodotto.id] || 0;
        const pianificato = prodotto.quantita_produzione || 0;
        const pct = pianificato > 0 ? Math.round((posizionato / pianificato) * 100) : null;
        const cont = this.contenitori_coltivazione.find((c) => c.id === prodotto.id_contenitore);
        const specie_ids = [...new Set((prodotto.componenti || []).map((c) => c.id_specie).filter(Boolean))];
        let nome_specie = 'Sconosciuta';
        if (specie_ids.length === 1) {
          const sp = this.specie_disponibili.find((s) => s.id == specie_ids[0]);
          nome_specie = sp ? sp.nome : `Specie ${specie_ids[0]}`;
        } else if (specie_ids.length > 1) {
          nome_specie = 'Multispecie';
        }
        const riepilogo = this._componenteRiepilogo(prodotto, cont);
        return {
          id: prodotto.id,
          label: (this.prodotto_labels[prodotto.id] || `P${prodotto.id}`).replace(/\s*Ø\d+.*$/, ''),
          img_url: this._varietaImgUrl(prodotto),
          color: this.productColor(prodotto),
          component_infos: this._componenteInfos(prodotto),
          tipo_contenitore: riepilogo.tipo_contenitore,
          specie_nomi: riepilogo.specie_nomi,
          righe_dettaglio: riepilogo.righe,
          diametro: cont ? cont.diametro : null,
          nome_specie,
          pianificato,
          posizionato,
          pct,
        };
      }).sort((a, b) => {
        const sc = a.nome_specie.localeCompare(b.nome_specie, 'it');
        if (sc !== 0) return sc;
        if (a.posizionato === 0 && b.posizionato > 0) return 1;
        if (b.posizionato === 0 && a.posizionato > 0) return -1;
        return (a.pct ?? 0) - (b.pct ?? 0);
      });
    },
    file_per_serra() {
      if (!this.stagione_attiva) return {};
      const map = {};
      for (const id of this.serre_ids_nel_layout) {
        map[id] = this.$store.getters.getFileStagione(id, this.stagione_attiva.id);
      }
      return map;
    },
    layout_corrente() {
      if (!this.stagione_attiva || !this.appezzamento_selezionato) return null;
      return this.$store.getters.getLayoutMappa(
        this.stagione_attiva.id,
        this.appezzamento_selezionato.id
      );
    },
    badge_label() {
      const l = this.layout_corrente;
      if (!l) return null;
      if (l.migrato) return 'Storico approssimativo';
      return 'Fork attivo';
    },
    badge_color() {
      if (!this.layout_corrente) return 'green darken-1';
      return this.layout_corrente.migrato ? 'orange darken-2' : 'green darken-1';
    },
    badge_icon() {
      if (!this.layout_corrente) return 'mdi-source-fork';
      return this.layout_corrente.migrato ? 'mdi-alert-outline' : 'mdi-source-fork';
    },
    posizioni_canvas() {
      if (!this.layout_corrente) {
        // Live: escludi posizioni di serre dismesse
        const dismesse = new Set(
          (this.$store.getters.getSerre || [])
            .filter(s => s.dismessa)
            .map(s => String(s.id))
        );
        return this.posizioni_appezzamento.filter(p => !dismesse.has(String(p.id_serra)));
      }
      // Fork: ignora completamente dismessa — le posizioni vengono dal layout storico
      return this.layout_corrente.serre.map((s) => ({
        id: s.id,
        id_appezzamento: this.appezzamento_selezionato.id,
        id_serra: s.id_serra_ref,
        x: s.x,
        y: s.y,
        rotazione: s.rotazione,
      }));
    },
    serre_canvas() {
      if (!this.layout_corrente) return this.serre;
      // In fork: usa tutte le serre (incluse dismesse) perché i fork storici le contengono ancora.
      // Le serre dismesse non nel fork non vengono mostrate.
      const tutte = this.$store.getters.getSerre || [];
      const fork_by_ref = {};
      for (const s of this.layout_corrente.serre) {
        fork_by_ref[String(s.id_serra_ref)] = s;
      }
      return tutte
        .filter(s => !s.dismessa || fork_by_ref[String(s.id)])
        .map((s) => {
          const fork = fork_by_ref[String(s.id)];
          if (fork) {
            return { ...s, nome: fork.nome, larghezza: fork.larghezza, lunghezza: fork.lunghezza, tipo: fork.tipo };
          }
          return s;
        });
    },
    blocchi_canvas() {
      if (!this.layout_corrente) return this.blocchi_appezzamento;
      return this.layout_corrente.blocchi || [];
    },
    fork_context_dialog() {
      if (!this.layout_corrente || !this.stagione_attiva || !this.appezzamento_selezionato) return null;
      return {
        id: this.layout_corrente.id,
        id_stagione: this.stagione_attiva.id,
        id_appezzamento: this.appezzamento_selezionato.id,
        serre: this.layout_corrente.serre || [],
      };
    },
    prodotti_in_mappa() {
      const ids = [...new Set(this.snapshot.map((p) => p.id_prodotto_pianificazione))];
      return ids.map((id) => {
        const prodotto = this.prodotto_map[id] || null;
        return {
          id,
          label: this.prodotto_labels[id] || `P${id}`,
          color: this.productColor(prodotto),
          img_url: this._varietaImgUrl(prodotto),
        };
      });
    },
    file_serra_ordinate() {
      return [...this.file_stagione_corrente].sort((a, b) => a.numero - b.numero);
    },
    recap_prodotti() {
      const totale_vasi = this.file_stagione_corrente
        .reduce((acc, f) => acc + (f.num_vasi || 0), 0);
      const ids_order = [];
      const per_prodotto = {};
      for (const pos of this.snapshot) {
        const id = pos.id_prodotto_pianificazione;
        if (per_prodotto[id] === undefined) {
          per_prodotto[id] = 0;
          ids_order.push(id);
        }
        per_prodotto[id] += pos.vaso_fine - pos.vaso_inizio + 1;
      }
      const occupati = ids_order.reduce((a, id) => a + per_prodotto[id], 0);
      const righe = ids_order.map((id) => {
        const prodotto = this.prodotto_map[id] || null;
        const vasi = per_prodotto[id];
        const cont = prodotto
          ? this.contenitori_coltivazione.find((c) => c.id === prodotto.id_contenitore)
          : null;
        const riepilogo = this._componenteRiepilogo(prodotto, cont);
        return {
          id,
          label: (this.prodotto_labels[id] || `P${id}`).replace(/\s*Ø\d+.*$/, ''),
          color: this.productColor(prodotto),
          img_url: this._varietaImgUrl(prodotto),
          component_infos: this._componenteInfos(prodotto),
          tipo_contenitore: riepilogo.tipo_contenitore,
          specie_nomi: riepilogo.specie_nomi,
          righe_dettaglio: riepilogo.righe,
          diametro: cont ? cont.diametro : null,
          vasi,
          pct: totale_vasi > 0 ? Math.round((vasi / totale_vasi) * 100) : 0,
        };
      }).sort((a, b) => b.vasi - a.vasi);
      return { righe, occupati, totale_vasi, liberi: totale_vasi - occupati };
    },
  },
  watch: {
    specie_disponibili: {
      immediate: true,
      handler(specie) {
        for (const sp of (specie || [])) {
          for (const v of (sp.variety || [])) {
            if (v.id_colore) this.$store.dispatch('fetchHexColore', v.id_colore);
          }
        }
      },
    },
    appezzamento_selezionato(app) {
      if (!app) return;
      this.loading_posizioni = true;
      const tasks = [
        this.$store.dispatch("fetchPosizioniAppezzamento", app.id),
        this.$store.dispatch("fetchBlocchiAppezzamento", app.id),
      ];
      if (this.stagione_attiva) {
        tasks.push(this._caricaLayout(this.stagione_attiva.id, app.id));
      }
      Promise.all(tasks).then(() => {
        this._fetchSnapshotSerre();
      }).finally(() => {
        this.loading_posizioni = false;
      });
    },
    layout_corrente(nuovo, vecchio) {
      if (nuovo && !vecchio) {
        // fork appena caricato/creato: aggiorna gli snapshot delle serre del fork
        this._fetchSnapshotSerre();
      }
    },
    stagione_attiva(nuova) {
      if (!nuova) return;
      this.$store.dispatch('fetchRiepilogoPosizionamento', nuova.id);
      if (this.appezzamento_selezionato) {
        this._caricaLayout(nuova.id, this.appezzamento_selezionato.id);
      }
      if (this.serra_selezionata) {
        this.loading_snapshot = true;
        // le file sono per (serra, stagione): senza questo fetch la vista serra
        // resterebbe senza file dopo un cambio stagione
        Promise.all([
          this.$store.dispatch("fetchSerraSnapshot", {
            id_serra: this.serra_selezionata.id,
            id_stagione: nuova.id,
          }),
          this.$store.dispatch("fetchFileStagione", {
            id_serra: this.serra_selezionata.id,
            id_stagione: nuova.id,
          }),
        ]).finally(() => { this.loading_snapshot = false; });
      }
      this._fetchSnapshotSerre();
    },
  },
  methods: {
    /* ---- navigazione drill-down ---- */
    onSerraSelezionataAppezzamento(id_serra) {
      const tutte = this.$store.getters.getSerre || [];
      this.serra_selezionata = tutte.find((s) => String(s.id) === String(id_serra)) || null;
      if (!this.serra_selezionata) return;
      this.vista = "serra";
      if (!this.stagione_attiva) return;
      this.loading_snapshot = true;
      Promise.all([
        this.$store.dispatch("fetchSerraSnapshot", {
          id_serra,
          id_stagione: this.stagione_attiva.id,
        }),
        this.$store.dispatch("fetchFileStagione", {
          id_serra,
          id_stagione: this.stagione_attiva.id,
        }),
      ]).finally(() => {
        this.loading_snapshot = false;
      });
    },

    /* ---- posizioni fork stagionale ---- */
    onPosizioneAppezzamentoAggiunta({ id_serra, cx, cy }) {
      if (!this.appezzamento_selezionato || !this.layout_corrente) return;
      this.$store.dispatch("addSerraFork", {
        id_layout: this.layout_corrente.id,
        id_stagione: this.stagione_attiva.id,
        id_appezzamento: this.appezzamento_selezionato.id,
        payload: { id_serra, x: cx, y: cy },
      }).catch(() => {
        this.mostraSnackbar("Errore durante il posizionamento.", "error");
      });
    },
    onPosizioneAppezzamentoAggiornata({ id, cx, cy, rotazione }) {
      if (!this.layout_corrente) return;
      this.$store.dispatch("updateSerraFork", {
        id_layout: this.layout_corrente.id,
        id_pos: id,
        id_stagione: this.stagione_attiva.id,
        id_appezzamento: this.appezzamento_selezionato.id,
        payload: { x: cx, y: cy, rotazione },
      }).catch(() => {
        this.mostraSnackbar("Errore durante il salvataggio della posizione.", "error");
      });
    },
    onPosizioneAppezzamentoRimossa(id_posizione) {
      if (!this.appezzamento_selezionato || !this.layout_corrente) return;
      this.$store.dispatch("removeSerraFork", {
        id_layout: this.layout_corrente.id,
        id_pos: id_posizione,
        id_stagione: this.stagione_attiva.id,
        id_appezzamento: this.appezzamento_selezionato.id,
      }).catch(() => {
        this.mostraSnackbar("Errore durante la rimozione.", "error");
      });
    },

    /* ---- layout stagionale ---- */
    // Unico punto di caricamento del fork. In caso di errore alza layout_errore
    // invece di lasciare layout_corrente a null: la vista mostra un banner di
    // retry e NON ricade sulla mappa aziendale (che sarebbe modificabile per errore).
    _caricaLayout(id_stagione, id_appezzamento) {
      this.layout_errore = false;
      return this.$store
        .dispatch('fetchLayoutMappa', { id_stagione, id_appezzamento })
        .catch(() => { this.layout_errore = true; });
    },
    riprovaCaricamentoLayout() {
      if (!this.stagione_attiva || !this.appezzamento_selezionato) return;
      this.loading_posizioni = true;
      this._caricaLayout(this.stagione_attiva.id, this.appezzamento_selezionato.id)
        .then(() => { if (!this.layout_errore) this._fetchSnapshotSerre(); })
        .finally(() => { this.loading_posizioni = false; });
    },

    /* ---- CRUD callbacks ---- */
    _fetchSnapshotSerre() {
      if (!this.stagione_attiva || !this.appezzamento_selezionato) return;
      const id_serre = this.serre_ids_nel_layout;
      if (!id_serre.length) return;
      this.$store.dispatch('fetchSnapshotsAppezzamento', {
        id_stagione: this.stagione_attiva.id,
        id_appezzamento: this.appezzamento_selezionato.id,
        id_serre,
      });
      for (const id_serra of id_serre) {
        if (!this.$store.getters.isFileStagioneCached(id_serra, this.stagione_attiva.id)) {
          this.$store.dispatch('fetchFileStagione', { id_serra, id_stagione: this.stagione_attiva.id });
        }
      }
    },

    /* ---- fork mappa stagionale ---- */
    async onForkMappa() {
      if (!this.stagione_attiva || !this.appezzamento_selezionato) return;
      this.loading_fork = true;
      try {
        await this.$store.dispatch('forkLayout', {
          id_stagione: this.stagione_attiva.id,
          id_appezzamento: this.appezzamento_selezionato.id,
        });
        this.mostraSnackbar('Mappa importata dalla mappa aziendale.', 'success');
      } catch (e) {
        if (e?.response?.status === 409) {
          // Il fork esiste già (creato altrove): ricarica invece di segnalare errore
          await this._caricaLayout(this.stagione_attiva.id, this.appezzamento_selezionato.id);
        } else {
          this.mostraSnackbar("Errore durante l'importazione della mappa.", 'error');
        }
      } finally {
        this.loading_fork = false;
      }
    },
    onModificaSerra(id_serra) {
      this.$refs.gestione_serre.openDialogConSerra(id_serra);
    },
    // I blocchi appartengono alla mappa aziendale (live). In presenza di un fork
    // stagionale sono uno snapshot read-only: scrivere qui corromperebbe la live.
    onBloccoAggiunto({ cx, cy, larghezza, lunghezza, rotazione, label, colore }) {
      if (!this.appezzamento_selezionato || this.layout_corrente) return;
      this.$store.dispatch("saveBloccoAppezzamento", {
        id_appezzamento: this.appezzamento_selezionato.id,
        payload: { x: cx, y: cy, larghezza, lunghezza, rotazione, label, colore },
      }).catch(() => {
        this.mostraSnackbar("Errore durante l'aggiunta del blocco.", "error");
      });
    },
    onBloccoAggiornato({ id, cx, cy, rotazione, larghezza, lunghezza, label, colore }) {
      if (this.layout_corrente) return;
      this.$store.dispatch("updateBloccoAppezzamento", {
        id_blocco: id,
        payload: { x: cx, y: cy, rotazione, larghezza, lunghezza, label, colore },
      }).catch(() => {
        this.mostraSnackbar("Errore durante il salvataggio del blocco.", "error");
      });
    },
    onBloccoRimosso(id_blocco) {
      if (!this.appezzamento_selezionato || this.layout_corrente) return;
      this.$store.dispatch("deleteBloccoAppezzamento", {
        id_blocco,
        id_appezzamento: this.appezzamento_selezionato.id,
      }).catch(() => {
        this.mostraSnackbar("Errore durante la rimozione del blocco.", "error");
      });
    },
    onSerreAggiornate() {
      if (this.serra_selezionata) {
        const tutte = this.$store.getters.getSerre || [];
        const aggiornata = tutte.find((s) => String(s.id) === String(this.serra_selezionata.id));
        if (aggiornata) this.serra_selezionata = aggiornata;
        else this.serra_selezionata = null;
      }
    },

    /* ---- fila / prodotto (vista serra) ---- */
    onFilaSelezionata(fila) {
      this.fila_selezionata   = fila;
      this.errore_posizione   = "";
      this.successo_posizione = false;
      this.dialog_picker      = true;
    },
    async onPosizioneSalvata(payload) {
      if (!this.serra_selezionata || !this.stagione_attiva) return;
      this.loading_posizione = true;
      this.errore_posizione = "";
      try {
        await this.$store.dispatch("savePosizioneSerra", {
          id_serra: this.serra_selezionata.id,
          id_stagione: this.stagione_attiva.id,
          payload,
        });
        this.$store.dispatch('fetchRiepilogoPosizionamento', this.stagione_attiva.id);
        this.successo_posizione = true;
        setTimeout(() => { this.successo_posizione = false; }, 3000);
      } catch (e) {
        this.errore_posizione =
          e?.response?.data?.message || "Errore durante il salvataggio.";
      } finally {
        this.loading_posizione = false;
      }
    },
    async onPosizioneEliminata({ id_posizione }) {
      if (!this.serra_selezionata || !this.stagione_attiva) return;
      try {
        await this.$store.dispatch("deletePosizioneSerra", {
          id_posizione,
          id_serra: this.serra_selezionata.id,
          id_stagione: this.stagione_attiva.id,
        });
        this.$store.dispatch('fetchRiepilogoPosizionamento', this.stagione_attiva.id);
      } catch {
        this.mostraSnackbar("Errore durante l'eliminazione.", "error");
      }
    },

    /* ---- utils ---- */
    // Restituisce tutti i colori hex distinti per ogni componente del prodotto
    _coloriProdotto(prodotto) {
      const colors = [];
      for (const c of (prodotto?.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        let hex = null;
        if (id_v) {
          const sp = this.specie_disponibili.find((s) => s.id == c.id_specie);
          if (sp) {
            const varieta = (sp.variety || []).find((v) => v.id == id_v);
            if (varieta?.id_colore) {
              hex = this.$store.getters.getHexByColoreId(varieta.id_colore);
            }
          }
        }
        const color = hex || this.productColor(prodotto);
        if (color && !colors.includes(color)) colors.push(color);
      }
      if (colors.length === 0) {
        const fallback = this.productColor(prodotto);
        if (fallback) colors.push(fallback);
      }
      return colors;
    },
    _hexColoreVarieta(prodotto) {
      for (const c of (prodotto?.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        if (!id_v) continue;
        const sp = this.specie_disponibili.find((s) => s.id == c.id_specie);
        if (!sp) continue;
        const varieta = (sp.variety || []).find((v) => v.id == id_v);
        if (!varieta?.id_colore) continue;
        const hex = this.$store.getters.getHexByColoreId(varieta.id_colore);
        if (hex) return hex;
      }
      return null;
    },
    _componenteInfos(prodotto) {
      const seen = new Set();
      const fallbacks = ['#EF9A9A', '#90CAF9', '#A5D6A7', '#FFE082'];
      const infos = [];
      for (const c of (prodotto?.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        const key = `${c.id_specie}|${id_v ?? ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const sp = this.specie_disponibili.find(s => s.id == c.id_specie);
        let img_url = null;
        if (sp && id_v) {
          const v = (sp.variety || []).find(v => v.id == id_v);
          if (v?.id_colore) img_url = `${process.env.VUE_APP_API_ROOT}/specie/img_colore/${v.id_colore}?t=${v.md5_colore || ''}`;
        }
        infos.push({ img_url, fallback: fallbacks[infos.length % fallbacks.length] });
        if (infos.length >= 4) break;
      }
      return infos;
    },
    _componenteRiepilogo(prodotto, cont) {
      const seen = new Set();
      const seen_specie = new Set();
      const righe = [];
      for (const c of (prodotto?.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        const id_s = (c.id_serie  && c.id_serie  !== 'None') ? c.id_serie  : null;
        const key = `${c.id_specie}|${id_s ?? ''}|${id_v ?? ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const sp = this.specie_disponibili.find(s => s.id == c.id_specie);
        if (sp?.nome) seen_specie.add(sp.nome);
        const ns = id_s ? this.nomeSerie(c.id_specie, id_s)   : null;
        const nv = id_v ? this.nomeVarieta(c.id_specie, id_v) : null;
        const v  = (id_v && sp) ? (sp.variety || []).find(vv => vv.id == id_v) : null;
        const hex = v?.id_colore ? this.$store.getters.getHexByColoreId(v.id_colore) : null;
        righe.push({ testo: [ns, nv].filter(Boolean).join(' · ') || 'Generico', hex });
        if (righe.length >= 4) break;
      }
      return {
        tipo_contenitore: cont?.tipo || null,
        specie_nomi: [...seen_specie].join(' / ') || null,
        righe,
      };
    },
    _sectorPath(i, n, r, cx, cy) {
      const a0 = (i / n) * 2 * Math.PI;
      const a1 = ((i + 1) / n) * 2 * Math.PI;
      const x0 = cx + r * Math.sin(a0);
      const y0 = cy - r * Math.cos(a0);
      const x1 = cx + r * Math.sin(a1);
      const y1 = cy - r * Math.cos(a1);
      const large = (a1 - a0) > Math.PI ? 1 : 0;
      return `M ${cx},${cy} L ${x0.toFixed(3)},${y0.toFixed(3)} A ${r},${r} 0 ${large},1 ${x1.toFixed(3)},${y1.toFixed(3)} Z`;
    },
    _varietaImgUrl(prodotto) {
      for (const c of (prodotto?.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        if (!id_v) continue;
        const sp = this.specie_disponibili.find((s) => s.id == c.id_specie);
        if (!sp) continue;
        const varieta = (sp.variety || []).find((v) => v.id == id_v);
        if (!varieta || !varieta.id_colore) continue;
        return `${process.env.VUE_APP_API_ROOT}/specie/img_colore/${varieta.id_colore}?t=${varieta.md5_colore || ''}`;
      }
      return null;
    },
    productColor(prodotto) {
      if (!prodotto) return "#E8E8E8";
      if (prodotto.colore_contenitore) {
        const parsed = this.parseColorEntry(prodotto.colore_contenitore);
        if (parsed && parsed.hex) return parsed.hex;
      }
      const palette = [
        "#4CAF50", "#2196F3", "#FF9800", "#E91E63", "#9C27B0",
        "#00BCD4", "#FF5722", "#795548", "#009688", "#3F51B5",
      ];
      return palette[(prodotto.id || 0) % palette.length];
    },
    mostraSnackbar(message, color) {
      this.snackbar = { visible: true, message, color };
    },
  },
};
</script>

<style scoped>
.legenda-swatch {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
  object-fit: cover;
}
</style>
